import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import ReleaseUpdateNotice, { type ReleaseNoticeKind } from "components/common/ReleaseUpdateNotice";
import { CLIENT_BUILD_ID, fetchPublishedRelease, releaseStore } from "./releaseClient";
import { decideUpdate, type ReleaseInfo, type ReleaseStore } from "./releaseLogic";

// 公開版を確認して、必要なら更新を案内する（docs/daytime_deployment_technical_design.md 6.2）。
// 起動時・タブ復帰時・保存成功後に確認する。タブ復帰の短時間の連続呼出しはまとめる。

const AUTO_RELOADED_KEY = "nitoel.release.autoReloadedTo";
const MIN_INTERVAL_MS = 30_000;

const readAutoReloaded = (): string | null => {
  try {
    return sessionStorage.getItem(AUTO_RELOADED_KEY);
  } catch {
    return null;
  }
};

const writeAutoReloaded = (buildId: string) => {
  try {
    sessionStorage.setItem(AUTO_RELOADED_KEY, buildId);
  } catch {
    // sessionStorage が使えない環境では記録できない。ループ防止のため自動更新しない側に倒す
  }
};

// SPA の router refresh ではなく公開 origin への document navigation で更新する
const defaultReload = () => window.location.assign(window.location.href);
const defaultGoTop = () => window.location.assign("/");

const ReleaseStoreContext = createContext<ReleaseStore>(releaseStore);

type Props = {
  children: ReactNode;
  store?: ReleaseStore;
  currentBuildId?: string;
  fetchRelease?: () => Promise<ReleaseInfo | null>;
  reload?: () => void;
  goTop?: () => void;
};

export const ReleaseGuardProvider = ({
  children,
  store = releaseStore,
  currentBuildId = CLIENT_BUILD_ID,
  fetchRelease = fetchPublishedRelease,
  reload = defaultReload,
  goTop = defaultGoTop,
}: Props) => {
  const [notice, setNotice] = useState<ReleaseNoticeKind | null>(null);
  const lastCheckedAt = useRef(0);
  const checking = useRef(false);

  const check = useCallback(
    async (force: boolean) => {
      // 配布工程を通さずに build した画面（build ID 無し）は公開版を取りに行かない。
      // 配布工程へ移行していない環境では公開版情報の配信経路も無く、要求が無駄になるため
      if (currentBuildId === "unknown") return;
      const now = Date.now();
      if (checking.current || (!force && now - lastCheckedAt.current < MIN_INTERVAL_MS)) return;
      checking.current = true;
      lastCheckedAt.current = now;
      try {
        const info = await fetchRelease();
        store.setPublished(info?.buildId ?? null);
        const decision = decideUpdate({
          ...store.snapshot(),
          currentBuildId,
          publishedBuildId: info?.buildId ?? null,
          autoReloadedTo: readAutoReloaded(),
        });
        if (decision === "reload" && info) {
          writeAutoReloaded(info.buildId);
          if (readAutoReloaded() === info.buildId) {
            reload();
            return;
          }
          setNotice((current) => current ?? "update");
        } else if (decision === "notify") {
          setNotice((current) => current ?? "update");
        }
      } finally {
        checking.current = false;
      }
    },
    [currentBuildId, fetchRelease, reload, store],
  );

  useEffect(() => {
    void check(true);
    const onVisible = () => {
      if (document.visibilityState === "visible") void check(false);
    };
    document.addEventListener("visibilitychange", onVisible);
    const unsubscribe = store.subscribe((event) => {
      if (event === "stale-contract") setNotice("stale");
      void check(true);
    });
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      unsubscribe();
    };
  }, [check, store]);

  return (
    <ReleaseStoreContext.Provider value={store}>
      {children}
      <ReleaseUpdateNotice kind={notice} onReload={reload} onGoTop={goTop} onClose={() => setNotice(null)} />
    </ReleaseStoreContext.Provider>
  );
};

// 未保存の入力がある間は自動更新しない。編集中フォーム・upload・画面外の編集を登録する
export const useUnsavedChanges = (key: string, dirty: boolean) => {
  const store = useContext(ReleaseStoreContext);
  useEffect(() => {
    store.setUnsaved(key, dirty);
    return () => store.setUnsaved(key, false);
  }, [store, key, dirty]);
};

// 画面が未保存状態の登録に対応していることを宣言する。宣言のない画面は自動更新せず案内だけにする
export const useAutoUpdateSupport = () => {
  const store = useContext(ReleaseStoreContext);
  useEffect(() => store.supportAutoUpdate(), [store]);
};
